import React from "react";

import { Loader, LoaderSizes } from "@hid-galaxy-ui/galaxy-react";

import { useNavigate } from "react-router";

import { useAppSelector } from "../../redux";

import {
  useSessionCanAccessModule,
  useSessionCanEditModule,
  useSessionModuleDenied,
} from "../../hooks/useSessionPermissions";

import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../utils/permissions/partnerManagementPermissionKeys";

import { ADMIN_HOME_DASHBOARD_PERMISSION_KEYS as HOME_KEYS } from "../../utils/permissions/adminHomeDashboardPermissionKeys";

import { SALES_ENGAGEMENT_PERMISSION_KEYS as SE_KEYS } from "../../utils/permissions/salesEngagementPermissionKeys";

import CreateNewPartnerModal from "../../pages/hid-admin/create-new-partner/create-new-partner-modal";

import AdminWelcomeHeader from "./components/welcome-header/AdminWelcomeHeader";

import ActionItemsToolbar from "./components/action-items/ActionItemsToolbar";

import ActionItemsCard from "./components/action-items/ActionItemsCard";

import AdminPendingActionsCard from "./components/pending-actions/AdminPendingActionsCard";

import PartnerEcosystemCard from "./components/partner-ecosystem/PartnerEcosystemCard";

import AdminPartnerProgramCard from "./components/partner-program/AdminPartnerProgramCard";

import PsRevenueCard from "./components/ps-revenue/PsRevenueCard";

import IntegrationOverviewCard from "./components/integration-overview/IntegrationOverviewCard";

import EndCustomerLaunchesSection from "./components/end-customer-launches/EndCustomerLaunchesSection";

import { STATIC_ACTION_ITEMS } from "./data/actionItemsConfig";

import { useAdminHomeDashboardData } from "./hooks/useAdminHomeDashboardData";
import { getPartnerEcosystemDimensionLabel } from "./utils/filterOptions";

import type { ActionItemId } from "./types";

import "./AdminHomeDashboard.scss";

const AdminHomeDashboardView: React.FC = () => {
  const navigate = useNavigate();

  const { loggedUserDetail } = useAppSelector((state) => state.auth);

  const [createPartnerModalOpen, setCreatePartnerModalOpen] =
    React.useState(false);

  const dashboardData = useAdminHomeDashboardData();

  const canAccessPartnerConfig = useSessionCanAccessModule(
    PM_KEYS.PARTNER_CONFIGURATIONS
  );
  const canEditPartnerConfig = useSessionCanEditModule(
    PM_KEYS.PARTNER_CONFIGURATIONS
  );

  const submitEngagementAccess = useSessionCanAccessModule(
    SE_KEYS.SUBMIT_ENGAGEMENT_FORM
  );

  const canEditSubmitEngagement = useSessionCanEditModule(
    SE_KEYS.SUBMIT_ENGAGEMENT_FORM
  );

  const manageEngagementsDenied = useSessionModuleDenied(
    SE_KEYS.MANAGE_ENGAGEMENTS
  );

  const showCreateEngagement =
    submitEngagementAccess && !manageEngagementsDenied;

  const canAccessPartnerProgram = useSessionCanAccessModule(
    HOME_KEYS.PARTNER_PROGRAM
  );
  const canEditPartnerProgram = useSessionCanEditModule(
    HOME_KEYS.PARTNER_PROGRAM
  );
  const canAccessPsRevenue = useSessionCanAccessModule(HOME_KEYS.PS_REVENUE);
  const canEditPsRevenue = useSessionCanEditModule(HOME_KEYS.PS_REVENUE);

  const visibleBottomBlocks =
    Number(canAccessPartnerProgram) + Number(canAccessPsRevenue);

  const actionItems = STATIC_ACTION_ITEMS.filter((item) => {
    if (item.id === "create-partner") return canAccessPartnerConfig;
    if (item.id === "create-engagement") return showCreateEngagement;
    return true;
  }).map((item) => {
    if (item.id === "create-partner" && !canEditPartnerConfig) {
      return { ...item, isStaticDisabled: true };
    }
    if (item.id === "create-engagement" && !canEditSubmitEngagement) {
      return { ...item, isStaticDisabled: true };
    }
    return item;
  });

  const handleActionClick = (id: ActionItemId) => {
    if (id === "create-partner" && canEditPartnerConfig) {
      setCreatePartnerModalOpen(true);
      return;
    }

    if (id === "create-engagement" && canEditSubmitEngagement) {
      navigate("/hid-admin/engagement-form");
    }
  };

  const handlePendingActionClick = (id: string) => {
    if (id === "engagement") {
      navigate("/hid-admin/dashboard/engagement-list");
      return;
    }

    if (id === "app-validation") {
      navigate("/hid-admin/dashboard/partner-organizations?tab=integration");
    }
  };

  if (dashboardData.isLoading) {
    return (
      <div className="admin-home-dashboard admin-home-dashboard--loading">
        <Loader variant={LoaderSizes.large} />
      </div>
    );
  }

  if (dashboardData.error) {
    return (
      <div className="admin-home-dashboard admin-home-dashboard--error">
        <p>{dashboardData.error}</p>
      </div>
    );
  }

  return (
    <div className="admin-home-dashboard">
      <div className="admin-home-dashboard__content">
        <AdminWelcomeHeader firstName={loggedUserDetail?.first_name ?? ""} />

        <ActionItemsToolbar
          basedOnOptions={dashboardData.basedOnOptions}
          filterOptionGroups={dashboardData.filterOptionGroups}
          isFilterEnabled={dashboardData.isFilterEnabled}
          basedOn={dashboardData.basedOn}
          filters={dashboardData.filters}
          onBasedOnChange={dashboardData.setBasedOn}
          onFiltersChange={dashboardData.setFilters}
        />

        <div className="admin-home-dashboard__top-row">
          <div className="admin-home-dashboard__left-col">
            <ActionItemsCard
              items={actionItems}
              onActionClick={handleActionClick}
            />

            <AdminPendingActionsCard
              actions={dashboardData.pendingActions}
              onActionClick={handlePendingActionClick}
            />
          </div>

          <div className="admin-home-dashboard__ecosystem-col">
            <PartnerEcosystemCard
              data={dashboardData.partnerEcosystem}
              dimensionColumnLabel={getPartnerEcosystemDimensionLabel(
                dashboardData.basedOn?.value
              )}
              showCreatePartner={canAccessPartnerConfig}
              createPartnerDisabled={!canEditPartnerConfig}
              onCreatePartner={() => setCreatePartnerModalOpen(true)}
              onManagePartners={() =>
                navigate("/hid-admin/dashboard/partner-organizations")
              }
            />

            <div
              className={`admin-home-dashboard__ecosystem-bottom-row${
                visibleBottomBlocks === 1
                  ? " admin-home-dashboard__ecosystem-bottom-row--single"
                  : ""
              }`}
            >
              {canAccessPartnerProgram ? (
                <AdminPartnerProgramCard
                  data={dashboardData.partnerProgram}
                  showReportLink={canEditPartnerProgram}
                />
              ) : null}

              {canAccessPsRevenue ? (
                <PsRevenueCard
                  data={dashboardData.psRevenue}
                  showReportLink={canEditPsRevenue}
                />
              ) : null}
            </div>
          </div>
        </div>

        {/* <IntegrationOverviewCard
          data={dashboardData.integrationOverview}
          onViewIntegrationStatus={() =>
            navigate("/hid-admin/dashboard/partner-organizations?tab=integration")
          }
        /> */}

        <IntegrationOverviewCard
          data={dashboardData.integrationOverview}
          onViewIntegrationStatus={() => {
            navigate(
              "/hid-admin/dashboard/partner-organizations?tab=integration"
            );
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 0);
          }}
        />

        <EndCustomerLaunchesSection />
      </div>

      {createPartnerModalOpen && (
        <CreateNewPartnerModal
          open={createPartnerModalOpen}
          onClose={() => setCreatePartnerModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminHomeDashboardView;
